import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TechnicalDataMainGroup } from '@1po/1po-bff-fe-spec/generated/catalog/technical_data/model/TechnicalDataMainGroup';
import { TechnicalDataSubGroup } from '@1po/1po-bff-fe-spec/generated/catalog/technical_data/model/TechnicalDataSubGroup';
import { History } from 'history';
import { RootState } from 'app/AppStore';
import { useFetchIAMTechnicalDataDetails, useUniversalProductsTree } from 'domains/catalog/Catalog.requests';
import {
  getVehicleTechnicalDataCategoryL3Data,
  getVehicleTechnicalDataCategoryL3Ids,
} from 'domains/catalog/Catalog.store';
import {
  MaintenanceTabsType,
  UNIVERSAL_PRODUCTS_NODE_ID,
  UNIVERSAL_PRODUCTS_OILS_NODE_ID,
} from 'domains/catalog/Catalog.types';
import {
  getOilViscosityNodeName,
  getUniversalProductsCatalogLevel2sourcePath,
  getUniversalProductsCatalogLevel3NodeId,
} from 'domains/catalog/Catalog.utils';
import { Flex, PaddingBox, Pipeline, Text } from 'UI';
import { getData } from 'utils';
import { SectionContent } from './DefaultMaintenance.styled';

interface DefaultMaintenanceTableProps {
  tab: MaintenanceTabsType | undefined;
  technicalData: TechnicalDataMainGroup | undefined;
}

const DefaultMaintenanceTable = ({ tab, technicalData }: DefaultMaintenanceTableProps) => {
  const categoryL3Ids = useSelector((state: RootState) =>
    getVehicleTechnicalDataCategoryL3Ids(state, technicalData?.id),
  );
  useFetchIAMTechnicalDataDetails(categoryL3Ids ?? [], technicalData?.id);
  if (!technicalData || !tab) {
    return <></>;
  }

  return (
    <SectionContent maxWidth={1600} direction={'column'}>
      {technicalData.subGroups.map((subGroup, index) => {
        return (
          <L3DataLine
            l3={subGroup}
            key={`${subGroup.id}-${index}`}
            isLastLine={index === technicalData.subGroups.length - 1}
          />
        );
      })}
    </SectionContent>
  );
};

const L3DataLine = ({ l3, isLastLine }: { l3: TechnicalDataSubGroup; isLastLine?: boolean }) => {
  const history = useHistory();
  const universalProductsTree = getData(useUniversalProductsTree());
  const lvl2tree = universalProductsTree
    ?.find((l1) => l1.nodeId === UNIVERSAL_PRODUCTS_NODE_ID)
    ?.childCategories?.find((l2) => l2.nodeId === UNIVERSAL_PRODUCTS_OILS_NODE_ID);
  const l3categoryData = useSelector((state: RootState) => getVehicleTechnicalDataCategoryL3Data(state, l3.id));
  const l3DataRaw = l3categoryData?.technicalDataContentList;
  const isFirstValueWithoutVariant = l3DataRaw && l3DataRaw[0].variant === undefined;
  const firstValueWithoutVariant = isFirstValueWithoutVariant ? l3DataRaw[0] : undefined;
  const l3Data = l3DataRaw?.filter((l3d) => l3d.variant !== undefined);

  return (
    <React.Fragment>
      <Flex>
        <Flex align={'center'} minHeight={54}>
          <Flex>
            <PaddingBox px={25}>
              <Text type="lg" bold>{`${firstValueWithoutVariant?.mountPositionName ?? l3.label}`}</Text>
            </PaddingBox>
          </Flex>
          {firstValueWithoutVariant && (
            <Flex>
              <PaddingBox px={15}>
                <Text type="base" bold>{`${firstValueWithoutVariant.value} ${
                  firstValueWithoutVariant.unit ?? ''
                }`}</Text>
              </PaddingBox>
            </Flex>
          )}
          {firstValueWithoutVariant?.additionalInformation ? (
            <Flex>
              <PaddingBox px={15}>{firstValueWithoutVariant.additionalInformation}</PaddingBox>
            </Flex>
          ) : (
            <Flex />
          )}
        </Flex>
      </Flex>
      {l3Data?.map((l3d, index) => {
        const viscosities = l3d?.value?.split('|').map((visc) => visc.trim());
        return (
          <Flex key={index}>
            <Flex align={'center'} minHeight={54}>
              <Flex>
                <PaddingBox px={25}>
                  <Text type="base">{`${l3d.mountPositionName ?? l3d.variant ?? ''}`}</Text>
                </PaddingBox>
              </Flex>
              <Flex>
                {l3d.value?.includes('|') ? (
                  <PaddingBox px={15}>
                    <Flex gap={10}>
                      {viscosities?.map((val) => {
                        const lvl3nodeId = getUniversalProductsCatalogLevel3NodeId(
                          lvl2tree,
                          getOilViscosityNodeName(val),
                        );
                        return lvl3nodeId ? (
                          <Text
                            key={val}
                            type={'light_14'}
                            displayStyle={'link'}
                            cursor={'pointer'}
                            hoverUnderLine
                            onClick={() => handleClick(history, lvl3nodeId)}
                          >
                            {val}
                          </Text>
                        ) : (
                          <Text key={val} type="base">
                            {val}
                          </Text>
                        );
                      })}
                    </Flex>
                  </PaddingBox>
                ) : (
                  <PaddingBox px={15}>
                    <Text type="base" bold>{`${l3d.value} ${l3d.unit ?? ''}`}</Text>
                  </PaddingBox>
                )}
              </Flex>
              {l3d.additionalInformation ? (
                <Flex>
                  <PaddingBox px={15}>{l3d.additionalInformation}</PaddingBox>
                </Flex>
              ) : (
                <Flex />
              )}
            </Flex>
          </Flex>
        );
      })}
      {!isLastLine && <Pipeline size={'100%'} horizontal />}
    </React.Fragment>
  );
};

const handleClick = (history: History, nodeId: string) => {
  const path = `${getUniversalProductsCatalogLevel2sourcePath()}/${nodeId}`;
  history.push(path);
};

export default DefaultMaintenanceTable;
